import { useEffect, type ReactNode } from 'react'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from './query-client'
import { ToastViewport } from '@/components/ui'
import { applyTheme, useUiStore } from '@/stores'
import { applyUiLanguage } from '@/internationalization/i18n'

/**
 * Every app-wide provider, in one place and in a fixed order.
 *
 * The theme effect also listens for OS changes so a user on 'system' follows
 * their machine at sunset without a reload.
 */
export function Providers({ children }: { children: ReactNode }) {
  const theme = useUiStore((s) => s.theme)
  const uiLanguage = useUiStore((s) => s.uiLanguage)

  useEffect(() => {
    applyUiLanguage(uiLanguage)
  }, [uiLanguage])

  useEffect(() => {
    applyTheme(theme)
    if (theme !== 'system') return
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => applyTheme('system')
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [theme])

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      <ToastViewport />
    </QueryClientProvider>
  )
}
